import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { recouvrementConfigService } from '../../api/services';
import { PromessePaiement, RelancePalier, TonRelance } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

const TONS: TonRelance[] = ['COURTOIS', 'FERME', 'MISE_EN_DEMEURE'];
const TON_LABELS: Record<TonRelance, string> = {
  COURTOIS: 'Courtois', FERME: 'Ferme', MISE_EN_DEMEURE: 'Mise en demeure',
};

/**
 * Recouvrement — relances configurables (TF-056) & promesses de paiement (TF-057).
 * Les paliers définissent quand et avec quel ton relancer ; une facture sous promesse active
 * n'est pas relancée par le planificateur.
 */
export function RelancesConfig() {
  const [paliers, setPaliers] = useState<RelancePalier[]>([]);
  const [promesses, setPromesses] = useState<PromessePaiement[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      recouvrementConfigService.getPaliers().then(setPaliers),
      recouvrementConfigService.getPromesses().then(setPromesses),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Relances & promesses</h1>
        <p className="page-subtitle">Paliers de relance et promesses de paiement (le planificateur les respecte)</p>
      </div>
      <PaliersEditor paliers={paliers} onChange={load} />
      <PromessesPanel promesses={promesses} onChange={load} />
    </div>
  );
}

function PaliersEditor({ paliers, onChange }: { paliers: RelancePalier[]; onChange: () => void }) {
  const [palier, setPalier] = useState('');
  const [jours, setJours] = useState('');
  const [ton, setTon] = useState<TonRelance>('COURTOIS');

  const add = async () => {
    if (palier === '' || jours === '') return;
    await recouvrementConfigService.createPalier({
      palier: Number(palier), joursApresEcheance: Number(jours), ton,
    });
    setPalier(''); setJours('');
    onChange();
  };
  const remove = async (id: number) => { await recouvrementConfigService.deletePalier(id); onChange(); };

  return (
    <div className="card">
      <h2 className="font-semibold mb-1">Paliers de relance</h2>
      <p className="text-xs text-gray-500 mb-3">
        À défaut de configuration, les paliers par défaut (J+3 / J+15 / J+30) s’appliquent.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Palier n°</label>
          <input className="input-field w-20" type="number" value={palier} onChange={(e) => setPalier(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Jours après échéance</label>
          <input className="input-field w-32" type="number" value={jours} onChange={(e) => setJours(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Ton</label>
          <select className="input-field" value={ton} onChange={(e) => setTon(e.target.value as TonRelance)}>
            {TONS.map((t) => <option key={t} value={t}>{TON_LABELS[t]}</option>)}
          </select>
        </div>
        <button onClick={add} className="btn-primary">Ajouter</button>
      </div>
      {paliers.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucun palier personnalisé.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {paliers.map((p) => (
            <li key={p.id} className="flex items-center justify-between py-2">
              <span className="text-gray-700">
                Palier {p.palier} — J+{p.joursApresEcheance} · {TON_LABELS[p.ton]}
              </span>
              <button onClick={() => remove(p.id)} className="text-red-600 hover:underline">Supprimer</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PromessesPanel({ promesses, onChange }: { promesses: PromessePaiement[]; onChange: () => void }) {
  const [docId, setDocId] = useState('');
  const [date, setDate] = useState('');
  const [montant, setMontant] = useState('');

  const add = async () => {
    if (docId === '' || date === '') return;
    await recouvrementConfigService.createPromesse({
      documentId: Number(docId), datePromesse: date, montant: montant === '' ? undefined : Number(montant),
    });
    setDocId(''); setDate(''); setMontant('');
    onChange();
  };
  const setStatut = async (id: number, statut: string) => {
    await recouvrementConfigService.updatePromesseStatut(id, statut); onChange();
  };
  const remove = async (id: number) => { await recouvrementConfigService.deletePromesse(id); onChange(); };

  return (
    <div className="card">
      <h2 className="font-semibold mb-1">Promesses de paiement</h2>
      <p className="text-xs text-gray-500 mb-3">
        Une facture sous promesse « En attente » dont la date n’est pas dépassée n’est pas relancée.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">N° document (id)</label>
          <input className="input-field w-28" type="number" value={docId} onChange={(e) => setDocId(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date promise</label>
          <input className="input-field" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Montant (option.)</label>
          <input className="input-field w-32" type="number" value={montant} onChange={(e) => setMontant(e.target.value)} />
        </div>
        <button onClick={add} className="btn-primary">Enregistrer</button>
      </div>
      {promesses.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucune promesse enregistrée.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-3 py-2">Facture</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Date promise</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {promesses.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <Link to={`/documents/view/${p.documentId}`} className="text-blue-600 hover:underline">{p.documentNumero}</Link>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{p.clientNom ?? '—'}</td>
                  <td className="px-3 py-2">{p.datePromesse}</td>
                  <td className="px-3 py-2 text-right">{p.montant != null ? <MoneyDisplay amount={p.montant} size="sm" /> : '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      p.statut === 'TENUE' ? 'bg-green-100 text-green-700'
                        : p.statut === 'NON_TENUE' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {p.statut === 'EN_ATTENTE' ? 'En attente' : p.statut === 'TENUE' ? 'Tenue' : 'Non tenue'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.statut === 'EN_ATTENTE' && (
                      <>
                        <button onClick={() => setStatut(p.id, 'TENUE')} className="text-green-600 hover:underline mr-2">Tenue</button>
                        <button onClick={() => setStatut(p.id, 'NON_TENUE')} className="text-red-600 hover:underline mr-2">Non tenue</button>
                      </>
                    )}
                    <button onClick={() => remove(p.id)} className="text-gray-400 hover:underline">Suppr.</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RelancesConfig;
